// Asking a customer to complete a statement, and following what happens next.
//
// ── THE LINK IS SHOWN ONCE ───────────────────────────────────────────────────
// `accessUrl` comes back on the call that mints it and never again — it is a
// bearer credential and the server stores only a digest. So it is held in local
// state for exactly as long as this panel is open, cleared when the panel
// closes, and never written anywhere that outlives the dialog. The same
// contract as any "copy your API key now" screen, and the screen says so.
//
// ── TWO TIMESTAMPS THAT ARE NOT THE SAME THING ───────────────────────────────
// "Link fetched" is `first_fetched_at` and very probably a mail scanner:
// Outlook, Proofpoint and Gmail all retrieve every URL in inbound mail before a
// human sees it. "Customer started" is `human_started_at` and requires a
// deliberate POST. Showing the first as "the customer opened it" would tell an
// operator the buyer is engaged when nothing of the sort has happened, so the
// labels here keep them apart and the help text says why.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { Copy, Link2, Mail, RotateCcw, ShieldOff } from 'lucide-react';
import {
  getComplianceRequest, issueComplianceRequest, resendComplianceRequest,
  revokeComplianceRequest, type ComplianceRequestStatus, type ComplianceRequestSummary,
  type IssuedComplianceRequest,
} from '@/lib/api';

const REQUEST_PILL: Record<ComplianceRequestStatus, string> = {
  draft: 'pill-default',
  active: 'pill-info',
  completed: 'pill-success',
  revoked: 'pill-default',
  expired: 'pill-warning',
  cancelled: 'pill-default',
};

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function ComplianceRequestPanel({
  submissionPublicId,
  submissionStatus,
  request,
  customerWorkflowEnabled,
  onChanged,
}: {
  submissionPublicId: string;
  submissionStatus: string;
  request: ComplianceRequestSummary | null;
  customerWorkflowEnabled: boolean;
  onChanged: () => void;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState(14);
  /** Held only while this panel is mounted. Never persisted. */
  const [issued, setIssued] = useState<IssuedComplianceRequest | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: detail } = useQuery({
    queryKey: ['compliance-request', request?.publicId],
    queryFn: () => getComplianceRequest(request!.publicId, () => getToken()),
    enabled: Boolean(request?.publicId),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] });
    void qc.invalidateQueries({ queryKey: ['compliance-requests'] });
    if (request?.publicId) {
      void qc.invalidateQueries({ queryKey: ['compliance-request', request.publicId] });
    }
    onChanged();
  }

  const issue = useMutation({
    mutationFn: () => issueComplianceRequest(submissionPublicId, {
      recipientEmail: email.trim(),
      recipientName: name.trim() || null,
      message: message.trim() || null,
      expiresInDays: days,
    }, () => getToken()),
    onSuccess: (r) => { setIssued(r); setError(null); refresh(); },
    onError: (e) => setError((e as { message?: string })?.message ?? 'Could not send the request'),
  });

  const resend = useMutation({
    mutationFn: () => resendComplianceRequest(request!.publicId, () => getToken()),
    onSuccess: (r) => { setIssued(r); setError(null); refresh(); },
    onError: (e) => setError((e as { message?: string })?.message ?? 'Could not resend'),
  });

  const revoke = useMutation({
    mutationFn: () => revokeComplianceRequest(request!.publicId, null, () => getToken()),
    onSuccess: () => { setIssued(null); setError(null); refresh(); },
    onError: (e) => setError((e as { message?: string })?.message ?? 'Could not revoke'),
  });

  async function copyLink() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.accessUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the link is selectable either way.
      setError('Could not copy automatically — select the link and copy it.');
    }
  }

  // The tenant kill switch. The server refuses regardless — this only stops an
  // operator writing out a request and a note before being told no.
  const canIssue = submissionStatus === 'draft' && !request && customerWorkflowEnabled;
  const canResend = request?.status === 'active';
  const canRevoke = request?.status === 'active';

  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-foreground-muted" aria-hidden />
        <h3 className="text-xs font-semibold">Customer request</h3>
        {request && (
          <span className={`pill ${REQUEST_PILL[request.status]}`}>
            {request.status}
          </span>
        )}
      </div>

      {/* The link, shown once. */}
      {issued && (
        <div className="rounded border border-[hsl(var(--warning))] bg-[hsl(var(--warning-muted))] p-3 mb-3">
          <p className="text-[11px] font-semibold">
            Copy this link now — it is shown once and cannot be retrieved again.
          </p>
          <p className="text-[11px] text-foreground-muted mt-0.5">
            {issued.accessUrlNote} It has also been emailed to the recipient.
          </p>
          <div className="flex items-start gap-2 mt-2">
            <code className="flex-1 min-w-0 text-[11px] break-all bg-surface-muted rounded px-2 py-1.5">
              {issued.accessUrl}
            </code>
            <button onClick={copyLink} className="btn-ghost text-xs flex-shrink-0">
              <Copy className="w-3.5 h-3.5" aria-hidden />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-[11px] text-foreground-muted mt-2">
            Treat it like a password: anyone holding it can complete this statement.
            Expires {new Date(issued.expiresAt).toLocaleDateString()}.
          </p>
        </div>
      )}

      {canIssue && (
        <div className="space-y-2">
          <p className="text-[11px] text-foreground-muted">
            Send the customer a secure link to complete this statement themselves. They do not
            need an account, and the form saves as they go.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium">Recipient email *</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm bg-surface border border-border rounded"
                placeholder="buyer@example.com"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium">Recipient name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm bg-surface border border-border rounded"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-medium">Note to the customer</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={1000}
              className="w-full mt-0.5 px-2 py-1.5 text-sm bg-surface border border-border rounded"
              placeholder="Why this is needed, and by when."
            />
            <span className="text-[11px] text-foreground-muted">
              Goes in the email. Do not put part numbers, end users or other order detail here —
              email is not a private channel.
            </span>
          </label>
          <label className="block">
            <span className="text-[11px] font-medium">Link valid for</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="mt-0.5 px-2 py-1.5 text-sm bg-surface border border-border rounded"
            >
              {[7, 14, 30, 60, 90].map((d) => (
                <option key={d} value={d}>{d} days</option>
              ))}
            </select>
          </label>
          <button
            onClick={() => issue.mutate()}
            disabled={!email.trim() || issue.isPending}
            className="btn-primary text-xs"
          >
            <Link2 className="w-3.5 h-3.5" aria-hidden />
            {issue.isPending ? 'Sending…' : 'Send secure link'}
          </button>
        </div>
      )}

      {request && (
        <div className="space-y-2">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <dt className="text-foreground-muted">Sent to</dt>
            <dd className="font-medium break-all">
              {request.recipientName ? `${request.recipientName} · ` : ''}{request.recipientEmail}
            </dd>
            <dt className="text-foreground-muted">Sent</dt>
            <dd>{when(request.issuedAt)}</dd>
            <dt className="text-foreground-muted">Expires</dt>
            <dd>{when(request.expiresAt)}</dd>
            <dt className="text-foreground-muted">Link fetched</dt>
            <dd>{when(request.firstFetchedAt)}</dd>
            <dt className="text-foreground-muted">Customer started</dt>
            <dd>{when(request.humanStartedAt)}</dd>
            <dt className="text-foreground-muted">Completed</dt>
            <dd>{when(request.completedAt)}</dd>
          </dl>

          <p className="text-[11px] text-foreground-muted">
            <span className="font-medium">&ldquo;Link fetched&rdquo; is not the customer.</span>{' '}
            Mail security scanners retrieve every link in an email before anyone reads it, so this
            often fills in within seconds of sending. &ldquo;Customer started&rdquo; needs a
            deliberate action by a person.
          </p>

          {detail && detail.credentials.length > 1 && (
            <p className="text-[11px] text-foreground-muted">
              {detail.credentials.length} links issued for this request. Only the most recent
              works; every earlier one stopped working when it was replaced.
            </p>
          )}

          {(canResend || canRevoke) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {canResend && (
                <button
                  onClick={() => resend.mutate()}
                  disabled={resend.isPending}
                  className="btn-ghost text-xs"
                >
                  <RotateCcw className="w-3.5 h-3.5" aria-hidden />
                  {resend.isPending ? 'Resending…' : 'Resend (new link)'}
                </button>
              )}
              {canRevoke && (
                <button
                  onClick={() => revoke.mutate()}
                  disabled={revoke.isPending}
                  className="btn-ghost text-xs text-destructive"
                >
                  <ShieldOff className="w-3.5 h-3.5" aria-hidden />
                  {revoke.isPending ? 'Revoking…' : 'Revoke link'}
                </button>
              )}
            </div>
          )}
          {canResend && (
            <p className="text-[11px] text-foreground-muted">
              Resending issues a fresh link and stops the old one immediately, including for
              anyone part-way through the form.
            </p>
          )}
        </div>
      )}

      {!canIssue && !request && (
        <p className="text-[11px] text-foreground-muted">
          {!customerWorkflowEnabled
            ? 'Sending statements to customers is switched off for this organization. '
              + 'Everything else on this page works as normal.'
            : submissionStatus === 'draft'
              ? 'Ready to send once you are happy with the prefilled values.'
              : 'This statement is no longer a draft, so there is nothing left to ask for.'}
        </p>
      )}

      {error && <p className="text-[11px] text-destructive mt-2">{error}</p>}
    </div>
  );
}
